import React, { useEffect, useState } from 'react';
import { Item } from '@/stores/canvasStore';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { SocialMediaCard } from './SocialMediaCard';
import { creativeMediaType, creativeToSocialContent, needsCreativeRehost } from '@/lib/socialCard';
import {
  ExternalLink,
  Eye,
  Heart,
  MessageCircle,
  Share2,
  Bookmark,
  TrendingUp,
  Sparkles,
  Loader2,
  Play,
} from 'lucide-react';

interface ScrapedCreativeContent {
  scraped_creative_id?: string;
  platform?: string;
  account_handle?: string;
  account_name?: string;
  content_url?: string;
  thumbnail_url?: string;
  /** The post's video, re-hosted in creative-media by apify-scrape */
  video_url?: string | null;
  /** width / height of the media, when known */
  media_aspect_ratio?: number | null;
  /** The pasted link, for posts pulled from a link card */
  source_url?: string;
  content_type?: string;
  hook_text?: string;
  caption?: string;
  views?: number;
  likes?: number;
  comments?: number;
  shares?: number;
  saves?: number;
  engagement_rate?: number;
  published_at?: string;
  // Motion-style scores
  creative_format?: string;
  hook_score?: number | null;
  hold_score?: number | null;
  engagement_score?: number | null;
  overall_creative_score?: number | null;
  hook_tactic?: string;
  messaging_angle?: string;
  target_persona?: string;
  why_selected?: string | null;
}

interface ScrapedCreativeCardProps {
  item: Item;
  /** fill: the canvas, card fills its box. natural: Discover, media sets its own height. */
  layout?: 'fill' | 'natural';
  /** Own border, rounding and shadow. Off inside a Discover tile that frames it. */
  framed?: boolean;
  /** Re-host an expired thumbnail in the background. Off for synced copies and the Pinned row. */
  autoRefresh?: boolean;
  /** Small tiles (Pinned): media and caption only; no chips, stats row or hover actions. */
  compact?: boolean;
}

const PLATFORM_LABELS: Record<string, string> = {
  tiktok: 'TikTok',
  instagram: 'Instagram',
  facebook: 'Facebook',
};

function formatNumber(num: number | undefined): string {
  if (!num) return '0';
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return num.toString();
}

function scoreColor(score: number | null | undefined): string {
  if (!score) return 'text-muted-foreground';
  if (score >= 80) return 'text-emerald-500';
  if (score >= 60) return 'text-green-500';
  if (score >= 40) return 'text-yellow-500';
  return 'text-red-500';
}

/** Dot colour for the score chip over the media. */
function scoreDot(score: number): string {
  if (score >= 80) return 'bg-emerald-400';
  if (score >= 60) return 'bg-green-400';
  if (score >= 40) return 'bg-yellow-400';
  return 'bg-red-400';
}

function formatFormatLabel(format: string | undefined): string {
  if (!format) return '';
  return format
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

const chip = 'h-7 px-2.5 rounded-full bg-black/45 backdrop-blur-md ring-1 ring-white/25 text-white text-[11px] font-semibold flex items-center gap-1.5 shadow-sm';
const actionButton = 'w-8 h-8 rounded-full bg-black/55 hover:bg-black/75 backdrop-blur-md flex items-center justify-center text-white transition-colors';

/**
 * Top Creatives card: the same image-first card as Instagram/TikTok links
 * (media at its ratio, gradient bar, glyph, play or carousel mark), with the
 * analysis kept secondary: score and engagement chips over the media, handle
 * and views/likes/comments in one slim row. The breakdown lives in the modal.
 */
export function ScrapedCreativeCard({ item, layout = 'fill', framed = layout === 'fill', autoRefresh = true, compact = false }: ScrapedCreativeCardProps) {
  const [showDetail, setShowDetail] = useState(false);
  // Opened from the play button: the video starts on its own
  const [autoPlay, setAutoPlay] = useState(false);
  const content = item.content as ScrapedCreativeContent;
  const cardContent = creativeToSocialContent(content);

  const engagementPct = content.engagement_rate
    ? (content.engagement_rate * 100).toFixed(1)
    : '0';

  const hasScores = content.overall_creative_score != null;
  const isAnalyzing = !hasScores;

  // Cards scraped before re-hosting point at expired CDN thumbnails: fetch a stored one once
  const [imageFailed, setImageFailed] = useState(false);
  const wantsRefresh = autoRefresh && (imageFailed || needsCreativeRehost(content));
  useEffect(() => {
    if (!wantsRefresh) return;
    import('@/lib/linkPreviewRefresh')
      .then(({ scheduleCreativeRefresh }) => scheduleCreativeRefresh(item))
      .catch((err) => console.warn('[ScrapedCreativeCard] refresh unavailable:', err));
    // item is read fresh from the store when the job runs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id, wantsRefresh]);

  const handleOpenUrl = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (content.content_url) {
      window.open(content.content_url, '_blank', 'noopener');
    }
  };

  const badge = (
    <div className="flex items-center gap-1.5" data-testid="creative-badge">
      {hasScores ? (
        <span className={chip} title="Creative score">
          <span className={cn('w-2 h-2 rounded-full', scoreDot(content.overall_creative_score!))} />
          {content.overall_creative_score}
        </span>
      ) : isAnalyzing ? (
        <span className={chip}>
          <Loader2 className="w-3 h-3 animate-spin" />
          Scoring
        </span>
      ) : null}
      <span className={chip} title="Engagement rate">
        <TrendingUp className="w-3 h-3" />
        {engagementPct}%
      </span>
    </div>
  );

  const actions = (
    <>
      <button
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); setAutoPlay(false); setShowDetail(true); }}
        className={actionButton}
        aria-label="Creative breakdown"
      >
        <Sparkles className="w-4 h-4" />
      </button>
      <button
        onMouseDown={(e) => e.stopPropagation()}
        onClick={handleOpenUrl}
        className={actionButton}
        aria-label={`Open on ${PLATFORM_LABELS[content.platform || ''] || 'the platform'}`}
      >
        <ExternalLink className="w-4 h-4" />
      </button>
    </>
  );

  // Tells a pulled post apart from a plain saved link at a glance
  const tag = (
    <span
      className={cn(chip, 'h-6 px-2 text-[10px] uppercase tracking-[0.08em]')}
      title="Pulled and scored with Top Creatives"
      data-testid="creative-tag"
    >
      <Sparkles className="w-3 h-3" />
      {!compact && 'Top Creative'}
    </span>
  );

  const handle = cardContent.authorHandle;
  const footer = (
    <div className="w-full flex items-center gap-2 text-[11px] text-muted-foreground tabular-nums" data-testid="creative-stats">
      {handle && <span className="font-medium text-foreground/80 truncate min-w-0">@{handle}</span>}
      <span className="ml-auto flex items-center gap-2.5 shrink-0">
        {!!content.views && <span className="flex items-center gap-1" title="Views"><Eye className="w-3 h-3" />{formatNumber(content.views)}</span>}
        <span className="flex items-center gap-1" title="Likes"><Heart className="w-3 h-3" />{formatNumber(content.likes)}</span>
        <span className="flex items-center gap-1" title="Comments"><MessageCircle className="w-3 h-3" />{formatNumber(content.comments)}</span>
      </span>
    </div>
  );

  return (
    <>
      <SocialMediaCard
        item={item}
        content={cardContent}
        layout={layout}
        framed={framed}
        autoRefresh={false}
        openOnClick={false}
        // Pinned tiles are too small for chips beside the play button
        badge={compact ? undefined : badge}
        actions={compact ? undefined : actions}
        footer={compact ? undefined : footer}
        tag={tag}
        onPlay={() => { setAutoPlay(true); setShowDetail(true); }}
        onImageError={() => setImageFailed(true)}
      />

      {/* Detail modal */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm">
              <Sparkles className="w-4 h-4" />
              Creative Breakdown — @{content.account_handle}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* The post itself: its video when there is one, else the cover */}
            <CreativeMedia item={item} content={content} autoPlay={autoPlay} />

            {/* Scores grid */}
            {hasScores && (
              <div className="grid grid-cols-4 gap-2">
                <ScoreCard label="Hook" score={content.hook_score!} />
                <ScoreCard label="Hold" score={content.hold_score!} />
                <ScoreCard label="Engagement" score={content.engagement_score!} />
                <ScoreCard label="Overall" score={content.overall_creative_score!} highlight />
              </div>
            )}

            {/* Classifications */}
            <div className="space-y-2">
              {content.creative_format && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground w-24 shrink-0">Format:</span>
                  <span className="font-medium">{formatFormatLabel(content.creative_format)}</span>
                </div>
              )}
              {content.hook_tactic && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground w-24 shrink-0">Hook Tactic:</span>
                  <span className="font-medium">{formatFormatLabel(content.hook_tactic)}</span>
                </div>
              )}
              {content.messaging_angle && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground w-24 shrink-0">Angle:</span>
                  <span className="font-medium">{formatFormatLabel(content.messaging_angle)}</span>
                </div>
              )}
              {content.target_persona && (
                <div className="flex items-start gap-2 text-sm">
                  <span className="text-muted-foreground w-24 shrink-0">Persona:</span>
                  <span>{content.target_persona}</span>
                </div>
              )}
            </div>

            {/* Hook text */}
            {content.hook_text && (
              <div className="space-y-1">
                <span className="text-xs font-medium text-muted-foreground">Hook</span>
                <p className="text-sm bg-muted/50 rounded-md p-2 italic">"{content.hook_text}"</p>
              </div>
            )}

            {/* Why selected */}
            {content.why_selected && (
              <div className="space-y-1">
                <span className="text-xs font-medium text-muted-foreground">Why This Is a Top Performer</span>
                <p className="text-sm">{content.why_selected}</p>
              </div>
            )}

            {/* Metrics detail */}
            <div className="space-y-1">
              <span className="text-xs font-medium text-muted-foreground">Performance Metrics</span>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <MetricCell icon={Eye} label="Views" value={formatNumber(content.views)} />
                <MetricCell icon={Heart} label="Likes" value={formatNumber(content.likes)} />
                <MetricCell icon={MessageCircle} label="Comments" value={formatNumber(content.comments)} />
                <MetricCell icon={Share2} label="Shares" value={formatNumber(content.shares)} />
                <MetricCell icon={Bookmark} label="Saves" value={formatNumber(content.saves)} />
                <MetricCell icon={TrendingUp} label="Eng. Rate" value={`${engagementPct}%`} />
              </div>
            </div>

            {/* Link to original */}
            {content.content_url && (
              <a
                href={content.content_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-primary hover:underline"
              >
                <ExternalLink className="w-4 h-4" />
                View original on {PLATFORM_LABELS[content.platform || ''] || content.platform}
              </a>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============================================================
// Sub-components
// ============================================================

type VideoState = 'ready' | 'loading' | 'expired' | 'missing';

/**
 * The creative in the breakdown: the stored video, playable. Cards pulled
 * before videos were stored ask apify-scrape to store it from the scraper row
 * they already have (no new scrape); when that URL has expired the cover
 * stays and links to the post.
 */
function CreativeMedia({ item, content, autoPlay }: { item: Item; content: ScrapedCreativeContent; autoPlay: boolean }) {
  const isVideo = creativeMediaType(content.content_type) === 'video';
  const [videoUrl, setVideoUrl] = useState<string | null>(content.video_url || null);
  const [state, setState] = useState<VideoState>(isVideo && !content.video_url ? 'loading' : 'ready');
  const platform = PLATFORM_LABELS[content.platform || ''] || 'the platform';

  useEffect(() => {
    if (!isVideo || videoUrl) return;
    let cancelled = false;
    (async () => {
      try {
        const { supabase } = await import('@/integrations/supabase/client');
        const { data, error } = await supabase.functions.invoke('apify-scrape', {
          body: { action: 'store_video', item_id: item.id },
        });
        const url = !error && typeof data?.video_url === 'string' ? data.video_url : null;
        if (cancelled) return;
        if (!url) {
          setState(!error && data?.reason === 'expired' ? 'expired' : 'missing');
          return;
        }
        setVideoUrl(url);
        setState('ready');
        // The server saved it on the card; mirror that locally so the next open plays at once
        const { useCanvasStore } = await import('@/stores/canvasStore');
        const patch = (i: Item) => (i.id === item.id ? { ...i, content: { ...(i.content as object), video_url: url } } : i);
        useCanvasStore.setState((s) => ({ items: s.items.map(patch), allItems: s.allItems.map(patch) }));
      } catch (err) {
        console.warn('[ScrapedCreativeCard] video unavailable:', err);
        if (!cancelled) setState('missing');
      }
    })();
    return () => {
      cancelled = true;
    };
    // Once per open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id]);

  if (videoUrl) {
    return (
      <video
        src={videoUrl}
        poster={content.thumbnail_url}
        controls
        playsInline
        autoPlay={autoPlay}
        preload="metadata"
        className="w-full max-h-[60vh] rounded-lg bg-black"
        data-testid="creative-video"
      />
    );
  }

  if (!content.thumbnail_url) return null;
  return (
    <div className="space-y-1.5">
      <div className="relative">
        <img
          src={content.thumbnail_url}
          alt=""
          className="w-full rounded-lg max-h-80 object-contain bg-muted"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
        {isVideo && state === 'loading' && (
          <div className="absolute inset-0 flex items-center justify-center gap-2 rounded-lg bg-black/40 text-white text-xs font-medium">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading video
          </div>
        )}
      </div>
      {isVideo && (state === 'expired' || state === 'missing') && content.content_url && (
        <a
          href={content.content_url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
          data-testid="creative-video-unavailable"
        >
          <Play className="w-3 h-3" />
          {state === 'expired' ? "The scraper's video link has expired." : 'No video was saved for this post.'} Watch it on {platform}.
        </a>
      )}
    </div>
  );
}

function ScoreCard({ label, score, highlight }: { label: string; score: number; highlight?: boolean }) {
  return (
    <div className={cn(
      'rounded-lg p-2 text-center',
      highlight ? 'bg-primary/10 border border-primary/20' : 'bg-muted/50'
    )}>
      <div className={cn('text-2xl font-bold', scoreColor(score))}>{score}</div>
      <div className="text-[10px] text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

function MetricCell({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string }) {
  return (
    <div className="flex items-center gap-1.5 bg-muted/30 rounded px-2 py-1.5">
      <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
      <div>
        <div className="text-xs font-medium">{value}</div>
        <div className="text-[10px] text-muted-foreground">{label}</div>
      </div>
    </div>
  );
}
